import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { 
  Ticket, Calendar, Search, Download, RefreshCw, 
  TrendingUp, DollarSign, BarChart3, Filter, CheckCircle, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VoucherIncome = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const { selectedBranch } = useApp();
  const [branchFilter, setBranchFilter] = useState(selectedBranch || 'all');
  const [branches, setBranches] = useState([]);

  // Sync with global topbar branch selector
  React.useEffect(() => {
    setBranchFilter(selectedBranch || 'all');
  }, [selectedBranch]);

  const isOwnerRole = user?.role?.toLowerCase() === 'owner';

  useEffect(() => {
    if (token) {
      fetchBranches();
      fetchVoucherIncome();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchVoucherIncome();
    }
  }, [dateRange, branchFilter]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchVoucherIncome = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/v2/voucher-revenue/report?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      if (branchFilter !== 'all') {
        url += `&branch_id=${branchFilter}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const rows = Array.isArray(response.data.data) ? response.data.data : (response.data.data?.transactions || []);
        // Filter rows by date range (backend ignores date params, so do client-side)
        const startD = dateRange.start;
        const endD = dateRange.end;
        const filtered = rows.filter(r => {
          const d = r.sale_date || r.date;
          return (!startD || d >= startD) && (!endD || d <= endD);
        });
        const total_amount = filtered.reduce((sum, r) => sum + (Number(r.total_revenue || r.amount || 0)), 0);
        const total_vouchers = filtered.reduce((sum, r) => sum + (Number(r.total_vouchers || r.quantity || 0)), 0);
        const total_used = filtered.reduce((sum, r) => sum + (Number(r.used_count || 0)), 0);
        const total_available = filtered.reduce((sum, r) => sum + (Number(r.available_count || 0)), 0);
        const total_expired = filtered.reduce((sum, r) => sum + (Number(r.expired_count || 0)), 0);
        const days = filtered.length;
        const avg_per_day = days > 0 ? total_amount / days : 0;
        // Map rows for table display
        const transactions = filtered.map((r, idx) => ({
          id: `${r.sale_date}-${idx}`,
          created_at: r.sale_date || r.date,
          description: r.description || `Generate ${r.total_vouchers || 0} voucher ${r.service_plan || ''}`.trim(),
          service_plan: r.service_plan || '-',
          branch_name: r.branch_name || '-',
          created_by_name: r.created_by_name || '-',
          owner_name: r.owner_name,
          quantity: r.total_vouchers || r.quantity || 0,
          amount: r.total_revenue || r.amount || 0,
          used_count: Number(r.used_count || 0),
          available_count: Number(r.available_count || 0),
          expired_count: Number(r.expired_count || 0)
        }));
        setData({
          summary: { total_amount, total_vouchers, total_used, total_available, total_expired, total_days: days, avg_per_day },
          transactions
        });
      }
    } catch (error) {
      console.error('Error fetching voucher income:', error);
      toast.error('Gagal memuat data pendapatan voucher');
    } finally {
      setLoading(false);
    }
  };

  // 2026-02-03: Fetch breakdown pendapatan per cabang untuk panel statistik
  const [byBranch, setByBranch] = useState([]);
  const [byBranchLoading, setByBranchLoading] = useState(false);
  useEffect(() => {
    if (!token) return;
    let stopped = false;
    const fetchByBranch = async () => {
      try {
        setByBranchLoading(true);
        let url = `${API_URL}/api/v2/voucher-revenue/by-branch?start_date=${dateRange.start}&end_date=${dateRange.end}`;
        if (branchFilter !== 'all') url += `&branch_id=${branchFilter}`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 });
        if (!stopped && res.data?.success) {
          setByBranch(Array.isArray(res.data.data) ? res.data.data : []);
        }
      } catch (_e) {
        if (!stopped) setByBranch([]);
      } finally {
        if (!stopped) setByBranchLoading(false);
      }
    };
    fetchByBranch();
    return () => { stopped = true; };
  }, [token, dateRange.start, dateRange.end, branchFilter]);

  // 2026-02-03: Trend pendapatan bulanan (12 bulan tahun terpilih) + tahunan
  const [trendYear, setTrendYear] = useState(new Date().getFullYear());
  const [trend, setTrend] = useState({ monthly: [], yearly: [] });
  const [trendLoading, setTrendLoading] = useState(false);
  useEffect(() => {
    if (!token) return;
    let stopped = false;
    const fetchTrend = async () => {
      try {
        setTrendLoading(true);
        let url = `${API_URL}/api/v2/voucher-revenue/trend?year=${trendYear}`;
        if (branchFilter !== 'all') url += `&branch_id=${branchFilter}`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, timeout: 25000 });
        if (!stopped && res.data?.success) {
          setTrend({ monthly: res.data.monthly || [], yearly: res.data.yearly || [] });
        }
      } catch (_e) {
        if (!stopped) setTrend({ monthly: [], yearly: [] });
      } finally {
        if (!stopped) setTrendLoading(false);
      }
    };
    fetchTrend();
    return () => { stopped = true; };
  }, [token, trendYear, branchFilter]);

  // 2026-02-03: Preset periode cepat
  const applyPresetPeriod = (kind) => {
    const now = new Date();
    const toISO = (d) => d.toISOString().split('T')[0];
    let start, end = toISO(now);
    if (kind === 'today') {
      start = end;
    } else if (kind === '7d') {
      const s = new Date(now); s.setDate(s.getDate() - 6); start = toISO(s);
    } else if (kind === 'month') {
      start = toISO(new Date(now.getFullYear(), now.getMonth(), 1));
    } else if (kind === 'year') {
      start = toISO(new Date(now.getFullYear(), 0, 1));
    }
    setDateRange({ start, end });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200">Pendapatan Voucher</h1>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            {isOwnerRole ? 'Laporan pendapatan voucher yang Anda generate' : 'Laporan pendapatan dari generate voucher hotspot'}
          </p>
        </div>
        <Button 
          onClick={fetchVoucherIncome} 
          variant="outline" 
          size="sm"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block dark:text-slate-300">Tanggal Mulai</label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="w-40"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block dark:text-slate-300">Tanggal Akhir</label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="w-40"
              />
            </div>
            {!isOwnerRole && (
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block dark:text-slate-300">Cabang</label>
                <select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm h-10 dark:border-slate-600"
                >
                  <option value="all">Semua Cabang</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {/* 2026-02-03: Preset periode cepat */}
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="text-[11px] text-slate-500 self-center mr-1">Cepat:</span>
            {[
              { k: 'today', label: 'Hari Ini' },
              { k: '7d',    label: '7 Hari' },
              { k: 'month', label: 'Bulan Ini' },
              { k: 'year',  label: 'Tahun Ini' },
            ].map(p => (
              <button
                key={p.k}
                type="button"
                onClick={() => applyPresetPeriod(p.k)}
                className="text-[11px] px-2.5 py-1 rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-300 dark:hover:border-emerald-400/60 transition"
                data-testid={`voucher-income-preset-${p.k}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Pendapatan</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(data?.summary?.total_amount || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center dark:bg-green-900/40">
                <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Voucher</p>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {(data?.summary?.total_vouchers || 0).toLocaleString('id-ID')}
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center dark:bg-indigo-900/40">
                <Ticket className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Sudah Dipakai</p>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {(data?.summary?.total_used || 0).toLocaleString('id-ID')}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center dark:bg-emerald-900/40">
                <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Belum Dipakai</p>
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                  {(data?.summary?.total_available || 0).toLocaleString('id-ID')}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center dark:bg-amber-900/40">
                <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 2026-02-03: Statistik Pendapatan per Cabang */}
      <Card className="mb-6" data-testid="voucher-revenue-by-branch">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-200">Pendapatan Voucher per Cabang</CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Perbandingan kontribusi setiap cabang pada periode terpilih</p>
            </div>
            {byBranchLoading && (
              <span className="text-[11px] text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3 animate-pulse" /> memuat…
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {byBranch.length === 0 && !byBranchLoading ? (
            <div className="text-center text-xs text-slate-500 py-4">Belum ada pendapatan voucher pada periode ini.</div>
          ) : (
            <>
              {(() => {
                const maxRev = byBranch.reduce((m, b) => Math.max(m, b.total_revenue || 0), 0) || 1;
                const grandRev = byBranch.reduce((s, b) => s + (b.total_revenue || 0), 0);
                return (
                  <div className="space-y-2">
                    {byBranch.map((b, idx) => {
                      const pct = maxRev > 0 ? Math.round((b.total_revenue / maxRev) * 100) : 0;
                      const share = grandRev > 0 ? ((b.total_revenue / grandRev) * 100).toFixed(1) : '0.0';
                      const isTop = idx === 0 && b.total_revenue > 0;
                      return (
                        <div key={`${b.branch_id || 'nb'}-${idx}`} className="p-3 bg-slate-50 rounded-lg dark:bg-slate-900" data-testid={`voucher-revenue-branch-${idx}`}>
                          <div className="flex items-center justify-between gap-3 mb-1.5">
                            <div className="min-w-0 flex-1 flex items-center gap-2">
                              {isTop && (
                                <span className="text-[9px] uppercase font-semibold bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 rounded px-1.5 py-0.5">Top</span>
                              )}
                              <span className="font-medium text-slate-800 dark:text-slate-200 truncate">{b.branch_name}</span>
                              <span className="text-[10px] text-slate-500 dark:text-slate-400">•</span>
                              <span className="text-[10px] text-slate-500 dark:text-slate-400">{b.total_vouchers.toLocaleString('id-ID')} voucher</span>
                              <span className="text-[10px] text-slate-500 dark:text-slate-400">•</span>
                              <span className="text-[10px] text-slate-500 dark:text-slate-400"><span className="text-blue-500">{b.used_count}</span> dipakai / <span className="text-emerald-500">{b.available_count}</span> ready / <span className="text-slate-400">{b.expired_count}</span> exp</span>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="font-bold text-green-600 dark:text-green-400 text-sm">{formatCurrency(b.total_revenue)}</div>
                              <div className="text-[10px] text-slate-400">{share}% total</div>
                            </div>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${isTop ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' : 'bg-emerald-400/70 dark:bg-emerald-500/70'}`}
                              style={{ width: `${Math.max(pct, 3)}%` }}
                            />
                          </div>
                          {b.revenue_used > 0 && b.revenue_used !== b.total_revenue && (
                            <div className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                              Ter-realisasi (voucher terpakai): {formatCurrency(b.revenue_used)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </CardContent>
      </Card>

      {/* 2026-02-03: Trend Pendapatan Bulanan & Tahunan */}
      <Card className="mb-6" data-testid="voucher-revenue-trend">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-200">Trend Pendapatan Bulanan &amp; Tahunan</CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Ringkasan pendapatan voucher per bulan &amp; per tahun</p>
            </div>
            <div className="flex items-center gap-2">
              {trendLoading && (
                <span className="text-[11px] text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3 animate-pulse" /> memuat…
                </span>
              )}
              <select
                value={trendYear}
                onChange={(e) => setTrendYear(Number(e.target.value))}
                className="text-xs px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 dark:text-slate-200"
                data-testid="voucher-trend-year-select"
              >
                {(() => {
                  const cy = new Date().getFullYear();
                  const arr = [];
                  for (let y = cy; y >= cy - 4; y--) arr.push(y);
                  return arr.map(y => <option key={y} value={y}>{y}</option>);
                })()}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            const monthly = trend.monthly || [];
            const yearly  = trend.yearly  || [];
            const maxMonthRev = monthly.reduce((m, x) => Math.max(m, x.total_revenue || 0), 0) || 1;
            const yearTotal = monthly.reduce((s, m) => s + (m.total_revenue || 0), 0);
            const yearVouchers = monthly.reduce((s, m) => s + (m.total_vouchers || 0), 0);
            const yearUsed = monthly.reduce((s, m) => s + (m.used_count || 0), 0);
            const prevYear = yearly.find(y => y.year === (trendYear - 1));
            const growthPct = prevYear && prevYear.total_revenue > 0
              ? ((yearTotal - prevYear.total_revenue) / prevYear.total_revenue) * 100
              : null;

            return (
              <div className="space-y-4">
                {/* Ringkasan tahun terpilih */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 bg-emerald-50/40 dark:bg-emerald-500/10 rounded-lg border border-emerald-100 dark:border-emerald-500/20">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">Total {trendYear}</div>
                    <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400" data-testid="voucher-trend-year-total">{formatCurrency(yearTotal)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">Voucher {trendYear}</div>
                    <div className="text-lg font-bold text-slate-800 dark:text-slate-200">{yearVouchers.toLocaleString('id-ID')}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">Sudah Dipakai</div>
                    <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{yearUsed.toLocaleString('id-ID')}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      vs {trendYear - 1}
                    </div>
                    {growthPct != null ? (
                      <div className={`text-lg font-bold ${growthPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {growthPct >= 0 ? '▲' : '▼'} {Math.abs(growthPct).toFixed(1)}%
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400">—</div>
                    )}
                  </div>
                </div>

                {/* Bar chart 12 bulan */}
                <div>
                  <div className="text-[11px] font-medium text-slate-600 dark:text-slate-300 mb-2">Pendapatan per Bulan ({trendYear})</div>
                  <div className="grid grid-cols-12 gap-1 items-end h-32 border-b border-slate-200 dark:border-slate-700 pb-1">
                    {monthly.map((m) => {
                      const h = maxMonthRev > 0 ? Math.max((m.total_revenue / maxMonthRev) * 100, 2) : 2;
                      const hasData = m.total_revenue > 0;
                      return (
                        <div key={m.month} className="relative group flex flex-col items-center justify-end h-full" data-testid={`voucher-trend-month-${m.month}`}>
                          <div
                            className={`w-full rounded-t transition-all ${hasData ? 'bg-gradient-to-t from-emerald-500 to-emerald-300 hover:from-emerald-600 hover:to-emerald-400' : 'bg-slate-200 dark:bg-slate-700'}`}
                            style={{ height: `${h}%` }}
                          />
                          {/* tooltip */}
                          <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 px-2 py-1 rounded bg-slate-900 text-white text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10 shadow-lg">
                            <div className="font-semibold">{m.month_name} {trendYear}</div>
                            <div>{formatCurrency(m.total_revenue)}</div>
                            <div className="text-slate-300">{m.total_vouchers.toLocaleString('id-ID')} voucher • {m.used_count} dipakai</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-12 gap-1 mt-1">
                    {monthly.map(m => (
                      <div key={`lbl-${m.month}`} className="text-[10px] text-slate-500 dark:text-slate-400 text-center">{m.month_name}</div>
                    ))}
                  </div>
                </div>

                {/* Recap per tahun */}
                {yearly.length > 0 && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-600 dark:text-slate-300 mb-2">Rekap Tahunan (5 tahun terakhir)</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                      {yearly.slice().reverse().map((y) => {
                        const active = y.year === trendYear;
                        return (
                          <button
                            key={y.year}
                            type="button"
                            onClick={() => setTrendYear(y.year)}
                            data-testid={`voucher-trend-year-card-${y.year}`}
                            className={`text-left p-3 rounded-lg border transition ${active ? 'bg-emerald-500/10 border-emerald-400 dark:border-emerald-400/60' : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-500/40'}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-semibold ${active ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-200'}`}>{y.year}</span>
                              {active && <span className="text-[9px] uppercase font-semibold bg-emerald-500 text-white rounded px-1.5 py-0.5">aktif</span>}
                            </div>
                            <div className="text-sm font-bold text-green-600 dark:text-green-400">{formatCurrency(y.total_revenue)}</div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                              {y.total_vouchers.toLocaleString('id-ID')} voucher • {y.used_count} dipakai
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* By Service Plan */}
      {data?.by_plan && data.by_plan.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-200">Per Paket Layanan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.by_plan.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg dark:bg-slate-900">
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-200">{item.plan}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{item.quantity} voucher</p>
                  </div>
                  <p className="font-bold text-green-600 dark:text-green-400">{formatCurrency(item.amount)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-200">Riwayat Transaksi</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-y border-slate-200 dark:bg-slate-900 dark:border-slate-700">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Tanggal</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Deskripsi</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Paket</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Cabang</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Dibuat Oleh</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Jumlah</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Status Voucher</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300">Total</th>
                </tr>
              </thead>
              <tbody>
                {data?.transactions && data.transactions.length > 0 ? (
                  data.transactions.map((trx) => (
                    <tr key={trx.id} className="border-b border-slate-100 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/50">
                      <td className="py-3 px-4 text-xs text-slate-600 dark:text-slate-300">
                        {formatDateTime(trx.created_at)}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-800 font-medium dark:text-slate-200">
                        {trx.description}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-600 dark:text-slate-300">
                        {trx.service_plan}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-600 dark:text-slate-300">
                        {trx.branch_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-600 dark:text-slate-300">
                        {trx.owner_name || trx.created_by_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-600 text-right dark:text-slate-300">
                        {trx.quantity} voucher
                      </td>
                      <td className="py-3 px-4 text-xs text-center">
                        <div className="flex flex-col items-center gap-1">
                          {trx.used_count > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {trx.used_count} Dipakai
                            </span>
                          )}
                          {trx.available_count > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                              <Clock className="w-3 h-3 mr-1" />
                              {trx.available_count} Tersedia
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs font-bold text-green-600 text-right dark:text-green-400">
                        {formatCurrency(trx.amount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-slate-500 dark:text-slate-400">
                      <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Tidak ada transaksi voucher pada periode ini</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VoucherIncome;
